import { ITagStatus } from '@tecalisdev/infinity';
import { BOOLEAN_STATUS, IOperationEvaluationDetail } from '../../processes/models/processes.models';
import { MenuItemAction } from '../../shared/models/master.models';
import { EVALUATION_TYPE, IOperationActivity } from '../models/operations.models';

export interface FinancialEvaluationActivityLogContext {
	startAt?: string | null;
	endAt?: string | null;
	logsUrl?: string | null;
	/** Título del modal JSON / cierre (EVALUATION vs ATTORNEY_POWERS). */
	responseTitleKey?: string;
}

export interface FinancialEvaluationActivityLogDeps {
	translate: (key: string, params?: Record<string, unknown>) => string;
	hasTranslation: (key: string) => boolean;
	getTagStatus: (state: string) => ITagStatus;
	notAvailableLabel: () => string;
}

/**
 * Filas de actividad Financial/Evaluation (sin attorney/SCRIPTURE, sin section / never-started).
 */
export function appendFinancialEvaluationActivityLogs(
	activity: IOperationActivity[],
	evaluation: IOperationEvaluationDetail,
	context: FinancialEvaluationActivityLogContext,
	deps: FinancialEvaluationActivityLogDeps,
): void {
	const startAt = context.startAt ?? evaluation.start_at;
	if (startAt) {
		activity.push({
			date: startAt,
			description: [deps.translate('PROCESSES.DETAIL.INIT_OPERATION')],
			...(context.logsUrl ? { showLogs: true } : {}),
		});
	}

	const typeKey = 'FINANCIAL_EVALUATION.' + (evaluation.type?.toUpperCase() ?? '');
	activity.push({
		title: deps.hasTranslation(typeKey) ? deps.translate(typeKey) : deps.notAvailableLabel(),
		icon: evaluation.mode === 'manual' ? 'icon-file' : 'icon-connection',
		status: evaluation.state as unknown as BOOLEAN_STATUS,
		description: evaluation.scoring
			? [deps.translate('PROCESSES.DETAIL.SCORING') + ': ' + evaluation.scoring]
			: [],
	});

	if (evaluation.evaluation) {
		activity.push({
			title:
				evaluation.evaluation === EVALUATION_TYPE.SCORING
					? deps.translate('PROCESSES.DETAIL.FINANCIAL_EVALUATION_OPTION')
					: deps.translate('PROCESSES.DETAIL.VERIFY_IDENTITY'),
			description:
				evaluation.evaluation === EVALUATION_TYPE.MATCH_DATA
					? [deps.translate('PROCESSES.DETAIL.EVALUATION_MATCH_DATA')]
					: [],
			icon: 'icon-resize-viewport',
			status: evaluation.state as unknown as BOOLEAN_STATUS,
		});
	}

	const endAt = context.endAt ?? evaluation.end_at;
	if (endAt) {
		const responseTitleKey = context.responseTitleKey ?? 'PROCESSES.DETAIL.EVALUATION';
		activity.push({
			description: [deps.translate('PROCESSES.DETAIL.FINISH_SCAN')],
			tag: evaluation.state
				? {
						label: deps.translate(`MASTER.STATUS.${evaluation.state.toUpperCase()}`),
						status: deps.getTagStatus(evaluation.state),
					}
				: undefined,
			date: endAt,
			link: {
				label: deps.translate('GENERAL.SHOW_RESPONSE'),
				action: MenuItemAction.SHOW_JSON,
				info: {
					title: deps.translate(responseTitleKey),
					data: evaluation,
				},
			},
		});
	}
}
